/**
 * @file useNotificationStream.js
 * @feature Notifications
 * @description Opens an SSE connection to the notifications stream for the
 * logged-in user. Replaces polling once the user profile is available.
 *
 * Usage:
 *   const { notifications, status, clearAll } = useNotificationStream(userId);
 *
 * - Automatically connects when `userId` is truthy.
 * - Automatically closes & cleans up on unmount or userId change.
 * - Handles reconnect with exponential back-off on unexpected close.
 * - Reads the id_token from sessionStorage (same pattern as useUserProfile).
 *
 * ✅ SSE BRIDGE: Every incoming payload is also dispatched as a global
 *    CustomEvent('sseTaskUpdate') so ProjectDetails can patch its SWR
 *    cache directly without any polling.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const SSE_BASE =
  process.env.REACT_APP_API_PROJECT_MANAGEMENT_API ??
  'https://34.160.16.181.nip.io/pm-api-apigee';

const MAX_RETRIES   = 5;
const BASE_DELAY_MS = 2_000;

/** Connection lifecycle states */
export const SSE_STATUS = {
  IDLE:         'idle',
  CONNECTING:   'connecting',
  CONNECTED:    'connected',
  RECONNECTING: 'reconnecting',
  ERROR:        'error',
  CLOSED:       'closed',
};

/**
 * Dispatches the raw SSE payload as a global CustomEvent so any page
 * (e.g. ProjectDetails) can listen without being coupled to this hook.
 *
 * ProjectDetails listens:
 *   window.addEventListener('sseTaskUpdate', (e) => { ... e.detail ... })
 */
const broadcastToApp = (payload) => {
  if (payload?.status === 'connected') return;
  // console.log('[SSE] Broadcasting to app:', payload); // ← add this temporarily
  window.dispatchEvent(new CustomEvent('sseTaskUpdate', { detail: payload }));
};

/**
 * @param {string|null|undefined} userId  – from user profile; hook is a no-op when falsy
 * @returns {{ notifications: object[], status: string, clearAll: Function, clearOne: Function }}
 */
export const useNotificationStream = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const [status, setStatus]               = useState(SSE_STATUS.IDLE);

  const esRef     = useRef(null);  // EventSource instance
  const retryRef  = useRef(0);     // retry count
  const timerRef  = useRef(null);  // back-off timer
  const activeRef = useRef(true);  // unmount guard

  const clearAll = useCallback(() => setNotifications([]), []);
  const clearOne = useCallback(
    (id) => setNotifications((prev) => prev.filter((n) => n.id !== id)),
    [],
  );

  useEffect(() => {
    if (!userId) return;
    activeRef.current = true;

    const connect = () => {
      if (!activeRef.current) return;

      const idToken = sessionStorage.getItem('id_token');
      // EventSource doesn't support custom headers, so token goes as query param.
      const url = `${SSE_BASE}/api/notifications/stream/${encodeURIComponent(userId)}?token=${encodeURIComponent(idToken ?? '')}`;

      setStatus(retryRef.current > 0 ? SSE_STATUS.RECONNECTING : SSE_STATUS.CONNECTING);

      const es = new EventSource(url);
      esRef.current = es;

      /* ── generic message (server sends `data: {...}`) ────────────────── */
      es.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);

          const notification = {
            id:        payload.id        ?? crypto.randomUUID(),
            type:      payload.type      ?? 'info',
            title:     payload.title     ?? 'Notification',
            message:   payload.message   ?? '',
            timestamp: payload.timestamp ?? new Date().toISOString(),
            read:      false,
            raw:       payload,
          };

          setNotifications((prev) => [notification, ...prev]);
          retryRef.current = 0;

          // ✅ BRIDGE: tell the rest of the app about this event
          broadcastToApp(payload);

        } catch {
          /* ignore malformed frames */
        }
      };

      /* ── named events the server may emit ───────────────────────────── */
      ['project_update', 'task_assigned', 'comment_added', 'alert', 'task-update'].forEach((eventName) => {
        es.addEventListener(eventName, (event) => {
          try {
            const payload = JSON.parse(event.data);

            const notification = {
              id:        payload.id        ?? crypto.randomUUID(),
              type:      eventName,
              title:     payload.title     ?? eventName.replace(/_/g, ' '),
              message:   payload.message   ?? '',
              timestamp: payload.timestamp ?? new Date().toISOString(),
              read:      false,
              raw:       payload,
            };

            setNotifications((prev) => [notification, ...prev]);
            retryRef.current = 0;

            // ✅ BRIDGE: named events also need to reach ProjectDetails
            broadcastToApp(payload);

          } catch {
            /* ignore malformed frames */
          }
        });
      });

      es.onopen = () => {
        if (!activeRef.current) return;
        retryRef.current = 0;
        setStatus(SSE_STATUS.CONNECTED);
      };

      es.onerror = () => {
        es.close();
        if (!activeRef.current) return;

        if (retryRef.current >= MAX_RETRIES) {
          setStatus(SSE_STATUS.ERROR);
          return;
        }

        const delay = BASE_DELAY_MS * 2 ** retryRef.current;
        retryRef.current += 1;
        setStatus(SSE_STATUS.RECONNECTING);
        timerRef.current = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      activeRef.current = false;
      clearTimeout(timerRef.current);
      esRef.current?.close();
      setStatus(SSE_STATUS.CLOSED);
    };
  }, [userId]);

  return { notifications, status, clearAll, clearOne };
};